import { onCLS, onINP, onLCP, onTTFB, onFCP, Metric } from 'web-vitals';

// Get page hierarchy level from pathname
const getPageLevel = (pathname: string): number => {
  const segments = pathname.split('/').filter(Boolean);
  if (pathname === '/') {
    return 0;
  }
  return Math.min(segments.length, 3);
};

// Format metric value
const formatMetricValue = (name: string, value: number): string => {
  switch (name) {
    case 'CLS':
      return value.toFixed(3);
    case 'INP':
    case 'LCP':
    case 'FCP':
    case 'TTFB':
      return `${Math.round(value)}ms`;
    default:
      return value.toString();
  }
};

// Send metrics to analytics endpoint
const sendToAnalytics = (metric: Metric) => {
  const body = {
    name: metric.name,
    value: metric.value,
    delta: metric.delta,
    id: metric.id,
    page: window.location.pathname,
    level: getPageLevel(window.location.pathname),
    formattedValue: formatMetricValue(metric.name, metric.value),
    timestamp: new Date().toISOString(),
  };

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    // Web Vitals logged to sessionStorage
  }

  // Send to analytics endpoint if available
  if (navigator.sendBeacon && process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT) {
    navigator.sendBeacon(
      process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT,
      JSON.stringify(body)
    );
  }

  // Store in session storage for debugging
  const storedMetrics = JSON.parse(
    sessionStorage.getItem('webVitals') || '[]'
  );
  storedMetrics.push(body);
  sessionStorage.setItem('webVitals', JSON.stringify(storedMetrics));
};

// Performance targets by page level
const performanceTargets = {
  0: { lcp: 2500, inp: 200, cls: 0.1 },  // Homepage
  1: { lcp: 2000, inp: 100, cls: 0.05 },  // Main sections
  2: { lcp: 1500, inp: 50, cls: 0.01 },  // Category hubs
  3: { lcp: 1000, inp: 40, cls: 0 },     // Content pages
};

// Check if metric meets target
const checkPerformanceTarget = (metric: Metric) => {
  const level = getPageLevel(window.location.pathname);
  const targets = performanceTargets[level as keyof typeof performanceTargets] || performanceTargets[3];
  
  let passed = true;
  let target = 0;
  
  switch (metric.name) {
    case 'LCP':
      target = targets.lcp;
      passed = metric.value <= target;
      break;
    case 'INP':
      target = targets.inp;
      passed = metric.value <= target;
      break;
    case 'CLS':
      target = targets.cls;
      passed = metric.value <= target;
      break;
  }
  
  if (!passed && process.env.NODE_ENV === 'development') {
    console.warn(
      `[Web Vitals] ${metric.name} exceeded target for level ${level}:`,
      `${formatMetricValue(metric.name, metric.value)} > ${formatMetricValue(metric.name, target)}`
    );
  }
  
  return { passed, target };
};

// Initialize Web Vitals monitoring
export const initWebVitals = () => {
  onCLS((metric) => {
    checkPerformanceTarget(metric);
    sendToAnalytics(metric);
  });
  
  onINP((metric) => {
    checkPerformanceTarget(metric);
    sendToAnalytics(metric);
  });
  
  onLCP((metric) => {
    checkPerformanceTarget(metric);
    sendToAnalytics(metric);
  });
  
  onFCP((metric) => {
    sendToAnalytics(metric);
  });
  
  onTTFB((metric) => {
    sendToAnalytics(metric);
  });
};

// Get current web vitals from session storage
export const getCurrentWebVitals = () => {
  const stored = sessionStorage.getItem('webVitals');
  return stored ? JSON.parse(stored) : [];
};

// Clear stored web vitals
export const clearWebVitals = () => {
  sessionStorage.removeItem('webVitals');
};